import { history, useLocation, useModel } from '@umijs/max';
import { useEffect, useSyncExternalStore } from 'react';
import { isAssetSharePath } from '@/utils/assetLibrary/publicShareService';
import { recoverAuthSession } from '@/utils/authApi';
import {
  getAuthSession,
  getAuthSessionGeneration,
  getAuthSessionIdentity,
  subscribeAuthSession,
} from '@/utils/authSession';
import { isLotteryScreenPath } from '@/utils/lotteryScreenRequest';
import {
  getPermissionGeneration,
  getPermissionRecoveryState,
  subscribePermissionRecovery,
  subscribePermissionSnapshot,
} from '@/utils/mockPermissions';

const sessionKey = () =>
  `${getAuthSessionGeneration()}:${getAuthSessionIdentity() ?? ''}`;

/** Also mounted by the layout's access fallback, where childrenRender is absent. */
export default function AuthSessionRecoverySync() {
  const { pathname } = useLocation();
  const { setInitialState } = useModel('@@initialState');
  const session = useSyncExternalStore(
    subscribeAuthSession,
    sessionKey,
    sessionKey,
  );
  const permission = useSyncExternalStore(
    subscribePermissionRecovery,
    getPermissionRecoveryState,
    getPermissionRecoveryState,
  );
  const generation = useSyncExternalStore(
    subscribePermissionSnapshot,
    getPermissionGeneration,
    getPermissionGeneration,
  );
  const excluded =
    pathname.startsWith('/user/') ||
    isAssetSharePath(pathname) ||
    isLotteryScreenPath(pathname);

  useEffect(() => {
    if (excluded) return;
    const auth = getAuthSession();
    const revision = `${session}:${generation}:${permission.status}`;
    setInitialState((previous) => {
      if (!previous || previous.authRecoveryRevision === revision) return previous;
      return {
        ...previous,
        currentUser: auth?.currentUser,
        activityProcessEntry:
          previous.currentUser?.id === auth?.currentUser?.id &&
          (!previous.authRecoveryRevision ||
            previous.authRecoveryRevision.startsWith(`${session}:`))
            ? previous.activityProcessEntry
            : undefined,
        authRecoveryRevision: revision,
      };
    });
    if (!auth) {
      history.replace(
        `/user/login?${new URLSearchParams({ redirect: window.location.pathname + window.location.search })}`,
      );
    }
  }, [excluded, session, generation, permission.status, setInitialState]);

  useEffect(() => {
    if (excluded) return undefined;
    const ensure = () => {
      if (document.visibilityState === 'visible') recoverAuthSession().catch(() => undefined);
    };
    // A failed lookup needs a user/event retry, not a remount-driven request loop.
    if (getPermissionRecoveryState().status !== 'error') ensure();
    window.addEventListener('focus', ensure);
    window.addEventListener('online', ensure);
    document.addEventListener('visibilitychange', ensure);
    return () => {
      window.removeEventListener('focus', ensure);
      window.removeEventListener('online', ensure);
      document.removeEventListener('visibilitychange', ensure);
    };
  }, [excluded, pathname, session]);
  return null;
}
